import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AppStore } from '@y42/app-state';
import { ErrorHandlerService } from '@y42/error-handling';
import { AbstractFeatureService } from '@y42/feature';
import { METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { Y42RouterService, Y42Routes } from '@y42/feature/routing';
import { AddEntityDialogResponse } from '@y42/feature/shared/add-entity';
import { GitQuery } from '@y42/git';
import { ThemeType, Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { assertNonNullable } from '@y42/util/assert';
import { VisualizationCanvasElementStore } from '../../state/canvas-element';
import {
  VisualizationDashboardGitService,
  VisualizationDashboardQuery,
  VisualizationDashboardSettingsGitModel,
  VisualizationDashboardState,
  VisualizationDashboardStore,
  VisualizationDashboardStoreModel,
  VisualizationDashboardUIModel,
} from '../../state/dashboard';
import { VisualizationTabQuery, VisualizationTabStore } from '../../state/tab';
import { builtInThemes, VisualizationThemeQuery } from '../../state/theme';
import { VisualizationCanvasElementService } from '../canvas-element/visualization-canvas-element.service';
import { VisualizationTabService } from '../tab/visualization-tab.service';
import { VisualizationThemeService } from '../theme/visualization-theme.service';
import { VisualizationWidgetService } from '../widget';
import { VisualizationDashboardAclService } from './visualization-dashboard-acl.service';
import { VisualizationDashboardUserDataService } from './visualization-dashboard-user-data.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class VisualizationDashboardService extends AbstractFeatureService<VisualizationDashboardState> {
  constructor(
    private readonly userData: VisualizationDashboardUserDataService,
    private readonly routerService: Y42RouterService,
    private readonly query: VisualizationDashboardQuery,
    private readonly store: VisualizationDashboardStore,
    protected readonly tabStore: VisualizationTabStore,
    protected readonly canvasElementStore: VisualizationCanvasElementStore,
    private readonly themeQuery: VisualizationThemeQuery,
    private readonly tabService: VisualizationTabService,
    private readonly tabQuery: VisualizationTabQuery,
    protected readonly acl: VisualizationDashboardAclService,
    protected readonly widget: VisualizationWidgetService,
    protected readonly toast: ToastService,
    protected readonly git: VisualizationDashboardGitService,
    protected readonly gitQuery: GitQuery,
    private readonly appStore: AppStore,
    private readonly themeService: VisualizationThemeService,
    private readonly canvasElementService: VisualizationCanvasElementService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    super({
      featureType: Y42FeatureType.VisualizationDashboard,
      store,
      query,
      userData,
      acl,
      git,
      gitQuery,
      toast,
      childStores: [tabStore, canvasElementStore],
    });
  }

  async create({ title, description }: AddEntityDialogResponse) {
    this.appStore.setStatus({ message: 'Creating dashboard' });
    const { id: $name, name } = this.query.getUniqueIdNamePair(title);
    const visualization = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name,
      description,
      name,
      settings: new VisualizationDashboardSettingsGitModel(),
    });
    await this.store.pending;
    this.store.setActive(visualization.$path);

    const selectedTheme = builtInThemes[4];
    assertNonNullable(selectedTheme);
    const defaultTheme = {
      type: ThemeType.BuiltIn,
      themeName: selectedTheme.themeName,
    } as const;

    await this.themeService.create({
      title: name,
      theme: defaultTheme,
    });

    const themeId = this.themeQuery.getActive()!.$path;

    const tab = await this.tabService.create('Tab 1', { skipNavigation: true });

    this.store.updateActive((active) => ({
      settings: {
        ...active.settings,
        theme: themeId,
      },
      $children: [tab.$path],
    }));

    this.appStore.clearStatus();
    await this.routerService.navigateToDetail(Y42Routes.VisualizationDashboard, $name);
  }

  async update(
    model: Partial<VisualizationDashboardUIModel>,
    $path: VisualizationDashboardUIModel['$path'] | undefined | null = this.query.getActiveId(),
  ) {
    if (!$path) {
      throw new Error(`[Visualization]: "${$path}" not found.`);
    }

    const visualization = { ...this.query.getEntity($path), ...model };
    this.store.update($path, visualization);
    await this.store.pending;
  }

  async updateSettings(
    settings: Partial<VisualizationDashboardUIModel['settings']>,
    $path: VisualizationDashboardUIModel['$path'] | undefined | null = this.query.getActiveId(),
  ) {
    if (!$path) {
      throw new Error(`[Visualization]: "${$path}" not found.`);
    }

    const entity = this.query.getEntity($path);

    assertNonNullable(entity);

    this.store.update($path, {
      settings: { ...entity.settings, ...settings },
    });
  }

  async delete($path = this.query.getActiveId()) {
    assertNonNullable($path);
    await this.store.delete($path);
    await this.store.pending;
  }

  override setActive($path: VisualizationDashboardStoreModel['$path'] | null) {
    this.store.setActive($path);

    if (!$path) {
      this.tabService.setActive(null);
      return;
    }

    const tabs = this.query.getActive()?.$children;

    if (!tabs?.length) {
      throw new Error(`[Visualization]: no active tabs found for "${$path}"`);
    }

    const firstTab = tabs[0];
    assertNonNullable(firstTab);
    this.tabService.setActive(firstTab);
  }

  async duplicate($path: VisualizationDashboardStoreModel['$path']) {
    const ref = this.toast.loading('Duplicating the dashboard...', {
      autoClose: false,
    });

    try {
      await this._duplicate($path);
      this.toast.success('Dashboard duplicated successfully');
    } catch (err) {
      this.toast.error('Dashboard duplication failed');
      this.errorHandlerService.error('Dashboard duplication failed', { context: { errorObject: err } });
    } finally {
      ref.close();
    }
  }

  private async _duplicate($path: VisualizationDashboardStoreModel['$path']) {
    const sourceDashboard = this.query.getEntity($path);
    if (!sourceDashboard) {
      throw new Error(`[Visualization-Dashboard]: cannot find dashboard to duplicate`);
    }

    const { id: $name, name } = this.query.getUniqueIdNamePair(sourceDashboard.name);
    const newDashboard = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name,
      description: sourceDashboard.description,
      name,
      settings: {
        ...sourceDashboard.settings,
        order: [],
      },
    });

    await this.store.pending;

    const theme = this.themeQuery.getEntity(sourceDashboard.settings.theme);
    assertNonNullable(theme);

    this.themeService.create({
      title: sourceDashboard.name,
      theme: theme.settings.theme,
    });

    const tabs = await Promise.all(
      // Duplicate the tabs based on the order array in the source dashboard.
      // This way only visible tabs will be duplicated.
      // See https://app.clickup.com/t/2575789/TECH-14355
      sourceDashboard.settings.order.map((tabPath) =>
        this.tabService.duplicate(tabPath as `src/Visualizations/Dashboards/${string}`, {
          parentDashboard: newDashboard,
          skipNavigation: true,
        }),
      ),
    );

    this.store.update(newDashboard.$path, {
      $children: tabs.map((tab) => tab.$path),
    });

    // Get the order of new tabs based on the original order.
    const order = tabs.map((tab) => tab.$path);

    this.updateSettings({ order }, newDashboard.$path);
  }

  updateTheme(themeId: string) {
    this.store.updateActive((active) => ({ settings: { ...active.settings, theme: themeId } }));
  }

  toggleCrossFilteringOnActiveDashboard() {
    this.store.updateActive(({ settings }) => ({
      settings: { ...settings, enable_cross_filtering: !settings.enable_cross_filtering },
    }));
  }
}
